import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { actorLandscapeService } from '../services/actorLandscapeService';

export default function LandscapeReportPage() {
  const { landscapeId } = useParams<{ landscapeId: string }>();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [title, setTitle] = useState('Landscape Report');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!landscapeId) return;

    // Primary path: data passed by the parent tab via localStorage (avoids auth in new tab)
    const key = `landscape_report_${landscapeId}`;
    const cached = localStorage.getItem(key);
    if (cached) {
      try {
        const { markdown, name } = JSON.parse(cached);
        localStorage.removeItem(key);   // consume immediately
        setTitle(name);
        setMarkdown(markdown ?? '*No report generated yet.*');
        return;
      } catch {
        localStorage.removeItem(key);
      }
    }

    // Fallback: direct URL access while authenticated (e.g. bookmark)
    actorLandscapeService.getById(landscapeId)
      .then(l => {
        setTitle(l.name);
        setMarkdown(l.report_markdown ?? '*No report generated yet.*');
      })
      .catch(() => setError('Failed to load report. Try opening it from the Landscape page.'));
  }, [landscapeId]);

  if (error) return (
    <div className="p-10 text-red-600 text-sm">{error}</div>
  );
  if (!markdown) return (
    <div className="p-10 text-slate-400 text-sm">Loading…</div>
  );

  return (
    <div className="max-w-5xl mx-auto px-8 py-10">
      <h1 className="text-2xl font-semibold text-slate-800 mb-6">{title}</h1>
      <div className="prose prose-slate max-w-none
        [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_table]:mt-3 [&_table]:mb-6
        [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-100 [&_th]:px-4 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold
        [&_td]:border [&_td]:border-slate-200 [&_td]:px-4 [&_td]:py-2 [&_td]:align-top
        [&_tr:nth-child(even)_td]:bg-slate-50
        [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-slate-800 [&_h2]:border-b [&_h2]:border-slate-200 [&_h2]:pb-1
        [&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:font-semibold [&_h3]:text-slate-700
        [&_ul]:my-2 [&_li]:my-0.5">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
      </div>
    </div>
  );
}
